/**
 * @fileoverview Resolve the public MCP SSE endpoint URL from build-time env.
 * @module features/mcp/utils/computeMcpUrl
 */

/**
 * Compute the public MCP endpoint URL.
 *
 * Spring AI's Streamable HTTP transport mounts a single endpoint at "/mcp"
 * (see backend application.yml: spring.ai.mcp.server.streamable-http.mcp-endpoint).
 * Both client→server requests (POST) and server→client streams (GET) hit it.
 *
 * Resolution order:
 *   1. If VITE_API_URL is absolute, strip a trailing "/api" and append "/mcp"
 *      (production: same host as the API).
 *   2. In Vite dev mode the FE runs on 5173 but the backend is on 8080 and
 *      "/mcp" is NOT proxied (only "/api" is), so use the backend port
 *      directly. Honors VITE_BACKEND_PORT for parity with vite.config.ts.
 *   3. Same-origin deployment fallback ("/mcp" on window.location.origin).
 */
export function computeMcpUrl(): string {
  const raw = (import.meta.env?.VITE_API_URL as string | undefined) ?? ''
  if (raw.startsWith('http')) {
    try {
      const url = new URL(raw)
      const trimmedPath = url.pathname.replace(/\/api\/?$/, '').replace(/\/$/, '')
      url.pathname = `${trimmedPath}/mcp`
      return url.toString()
    } catch {
      /* fall through */
    }
  }
  if (import.meta.env?.DEV) {
    const port = (import.meta.env?.VITE_BACKEND_PORT as string | undefined) ?? '8080'
    return `http://localhost:${port}/mcp`
  }
  if (typeof window === 'undefined') return '/mcp'
  return `${window.location.origin}/mcp`
}
